import { React, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DataTable from "examples/Tables/DataTable";
import { ToastContainer, toast } from "react-toastify";
import { Stack, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import { useNavigate } from "react-router-dom";
import MDBadge from "components/MDBadge";
import nodata from "assets/images/nodata.png";
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { hasAdmissionPermission } from "hooks/permissions";
const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

function AllowApplication() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cnic, setCnic] = useState("");
  const [applications, setApplications] = useState([]);
  
  // Allow Modal States
  const [openAllowModal, setOpenAllowModal] = useState(false);
  const [currentApplicationForAllow, setCurrentApplicationForAllow] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [allowLoading, setAllowLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  
  // View Modal States
  const [openViewModal, setOpenViewModal] = useState(false);
  const [currentApplicationForView, setCurrentApplicationForView] = useState(null);
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [disallowLoading, setDisallowLoading] = useState(false);

  const formatCNIC = (value) => {
    // Remove all non-digits
    const numbers = value.replace(/\D/g, '');
    
    // Enforce exact format: 5-7-1 digits
    if (numbers.length <= 5) {
      return numbers;
    } else if (numbers.length <= 12) {
      return `${numbers.slice(0, 5)}-${numbers.slice(5, Math.min(12, numbers.length))}`;
    } else {
      return `${numbers.slice(0, 5)}-${numbers.slice(5, 12)}-${numbers.slice(12, 13)}`;
    }
  };

  const handleCnicChange = (e) => {
    const inputValue = e.target.value;
    // Only allow numbers and dashes
    if (!/^[\d-]*$/.test(inputValue)) {
      return;
    }
    const formattedValue = formatCNIC(inputValue);
    setCnic(formattedValue);
  };

  // Allow Modal Functions
  const handleOpenAllowModal = async (application) => {
    setCurrentApplicationForAllow(application);
    setOpenAllowModal(true);
    await fetchCampaigns();
  };

  const handleCloseAllowModal = () => {
    setOpenAllowModal(false);
    setCurrentApplicationForAllow(null);
    setSelectedCampaign("");
    setCampaigns([]);
  };

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        `${BASE_URL}/campaign/get_campaigns`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        setCampaigns(data.campaigns || []);
      } else {
        toast.error(data.error || "Failed to fetch campaigns");
        setCampaigns([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching campaigns");
    } finally {
      setCampaignsLoading(false);
    }
  };

  const handleAllowApplication = async () => {
    if (!selectedCampaign || !currentApplicationForAllow) {
      toast.error("Please select a campaign");
      return;
    }

    setAllowLoading(true);
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: selectedCampaign,
          user_id: currentApplicationForAllow.id
        }),
      };

      const response = await fetch(
        `${BASE_URL}/campaign/campaign_applicants_allow`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        toast.success("Application allowed successfully");
        handleCloseAllowModal();
      } else {
        toast.error(data.error || "Failed to allow application");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while allowing application");
    } finally {
      setAllowLoading(false);
    }
  };

  // View Modal Functions
  const handleOpenViewModal = async (application) => {
    setCurrentApplicationForView(application);
    setOpenViewModal(true);
    await fetchUserCampaigns(application.id);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setCurrentApplicationForView(null);
    setUserCampaigns([]);
  };

  const fetchUserCampaigns = async (userId) => {
    setViewLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        `${BASE_URL}/campaign/campaign_applicants_user/${userId}`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        setUserCampaigns(data || []);
      } else {
        toast.error(data.error || "Failed to fetch user campaigns");
        setUserCampaigns([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching user campaigns");
    } finally {
      setViewLoading(false);
    }
  };

  const handleDisallowApplication = async (userId) => {
    setDisallowLoading(true);
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        `${BASE_URL}/campaign/campaign_applicants_disallow/${userId}`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        toast.success("Application disallowed successfully");
        // Refresh the user campaigns list
        await fetchUserCampaigns(userId);
      } else {
        toast.error(data.error || "Failed to disallow application");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while disallowing application");
    } finally {
      setDisallowLoading(false);
    }
  };

  const fetchApplicationsByCnic = async () => {
    // Check for exact CNIC format: 5-7-1 digits
    const cnicRegex = /^(\d{5})-(\d{7})-(\d{1})$/;
    if (!cnicRegex.test(cnic)) {
      toast.error("Please enter CNIC in correct format: XXXXX-XXXXXXX-X");
      return;
    }

    setLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        `${BASE_URL}/auth/get_applicants_by_cnic?cnic=${cnic}`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        setApplications(data.applicants || []);
        if (data.applicants?.length === 0) {
          toast.info("No applications found for this CNIC");
        }
      } else {
        toast.error(data.error || "Application not found");
        setApplications([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching applications");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Applicant Name", accessor: "applicant_name", align: "center" },
    { Header: "email", accessor: "email", align: "center" },
    { Header: "CNIC", accessor: "cnic", align: "center" },
    { Header: "Phone", accessor: "phone", align: "center" },
    {
      Header: "Actions", accessor: "actions", align: "center"
    },
  ];

  const rows = applications.length > 0
    ? applications.map((app, index) => ({
        serial: index + 1,
        id: app.id,
        applicant_name: app.name,
        email: app.email,
        cnic: app.cnic,
        phone: app.phone,
        actions: (
          <>
            {hasAdmissionPermission("AllowApplication", "UPDATE") && (
              <IconButton 
                onClick={() => handleOpenAllowModal(app)}
                color="success"
                title="Allow Application"
              >
                <CheckCircleIcon />
              </IconButton>
            )}
            {hasAdmissionPermission("AllowApplication", "UPDATE") && (
              <IconButton
                onClick={() => handleOpenViewModal(app)}
                color="info"
                title="View Application"
              >
                <VisibilityIcon />
              </IconButton>
            )}
          </>
        ),
      }))
    : [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Search Applicants by CNIC
                </MDTypography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Enter CNIC"
                      variant="outlined"
                      value={cnic}
                      onChange={handleCnicChange}
                      placeholder="XXXXX-XXXXXXX-X"
                      inputProps={{ 
                        maxLength: 15,
                        pattern: "\\d{5}-\\d{7}-\\d{1}"
                      }}
                      helperText="Format: XXXXX-XXXXXXX-X"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={fetchApplicationsByCnic}
                      disabled={loading}
                      fullWidth
                      style={{ color: "white" }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : "Fetch"}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <Card sx={{ mt: 3 }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Applicants Results
                </MDTypography>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress />
                  </MDBox>
                ) : applications.length > 0 ? (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                    <img
                      src={nodata}
                      style={{
                        width: "20%",
                        height: "20%",
                      }}
                      alt="No Data"
                    />
                    <MDTypography variant="body1" mt={2}>
                      No Applicants found. Please enter a valid CNIC and try again.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Allow Modal */}
      <Modal
        open={openAllowModal}
        onClose={handleCloseAllowModal}
        aria-labelledby="allow-application-modal"
        aria-describedby="modal-to-allow-application"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
        }}>
          <MDTypography variant="h6" component="h2" gutterBottom>
            Allow Application for Campaign
          </MDTypography>
          
          {currentApplicationForAllow && (
            <MDBox mb={3}>
              <MDTypography variant="body2" color="text.secondary">
                Applicant: {currentApplicationForAllow.name}<br/>
                Email: {currentApplicationForAllow.email}<br/>
                CNIC: {currentApplicationForAllow.cnic}
              </MDTypography>
            </MDBox>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Campaign</InputLabel>
            <Select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              style={{height: "30px"}}
              label="Select Campaign"
              disabled={campaignsLoading}
            >
              {campaignsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} /> Loading campaigns...
                </MenuItem>
              ) : (
                campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name || `Campaign ${campaign.id}`}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <DialogActions>
            <Button onClick={handleCloseAllowModal}>Cancel</Button>
            <Button 
              onClick={handleAllowApplication} 
              variant="contained" 
              color="primary"
              style={{ color: "white" }}
              disabled={!selectedCampaign || allowLoading}
            >
              {allowLoading ? (
                <CircularProgress size={24} style={{ color: "white" }} />
              ) : (
                "Allow Application"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Modal>

      {/* View Modal */}
      <Modal
        open={openViewModal}
        onClose={handleCloseViewModal}
        aria-labelledby="view-application-modal"
        aria-describedby="modal-to-view-application"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <MDTypography variant="h6" component="h2" gutterBottom>
            Application Campaign Status
          </MDTypography>
          
          {currentApplicationForView && (
            <MDBox mb={3}>
              <MDTypography variant="body2" color="text.secondary">
                Applicant: {currentApplicationForView.name}<br/>
                Email: {currentApplicationForView.email}<br/>
                CNIC: {currentApplicationForView.cnic}
              </MDTypography>
            </MDBox>
          )}

          {viewLoading ? (
            <MDBox display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </MDBox>
          ) : userCampaigns.length > 0 ? (
            <MDBox>
              <MDTypography variant="subtitle1" fontWeight="medium" mb={2}>
                Campaign Applications:
              </MDTypography>
              {userCampaigns.map((campaign, index) => (
                <Card key={campaign.id || index} sx={{ mb: 2, p: 2 }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox>
                      <MDTypography variant="body1" fontWeight="medium">
                        {campaign.campaign_name || `Campaign ${campaign.id}`}
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        Status: {campaign.is_allowed === 1 ? 
                          <MDBadge variant="contained" color="success" badgeContent="Allowed" size="sm" /> :
                          <MDBadge variant="contained" color="error" badgeContent="Not Allowed" size="sm" />
                        }
                      </MDTypography>
                    </MDBox>
                    {campaign.is_allowed === 1 && (
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ color: "white" }}
                        size="small"
                        onClick={() => handleDisallowApplication(currentApplicationForView.id)}
                        disabled={disallowLoading}
                        startIcon={<BlockIcon />}
                      >
                        {disallowLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Disallow"
                        )}
                      </Button>
                    )}
                  </MDBox>
                </Card>
              ))}
            </MDBox>
          ) : (
            <MDBox textAlign="center" p={3}>
              <MDTypography variant="body1" color="text.secondary">
                No campaign applications found for this user.
              </MDTypography>
            </MDBox>
          )}

          <DialogActions>
            <Button onClick={handleCloseViewModal}>Close</Button>
          </DialogActions>
        </Box>
      </Modal>
    </DashboardLayout>
  );
}

export default AllowApplication;